// Version checking configuration
const VERSION_CHECK_CONFIG = {
  CHECK_INTERVAL: 5 * 60 * 1000, // Check every 5 minutes
  STATIC_VERSION_FILE: '/version.json', // Static version file
};

// Initialize version from version.json on app startup
let currentVersionCache = null;

export const initializeVersion = async () => {
  try {
    const response = await fetch('/version.json?t=' + Date.now(), {
      method: 'GET',
      cache: 'no-cache'
    });
    
    if (response.ok) {
      const versionInfo = await response.json();
      currentVersionCache = versionInfo.version;
      
      // Store in localStorage for future reference
      localStorage.setItem('app_version_info', JSON.stringify(versionInfo));
      
      console.log('✅ Version initialized:', currentVersionCache);
      return currentVersionCache;
    }
  } catch (error) {
    console.warn('Failed to initialize version from version.json:', error);
  }
  
  // Fallback to package.json version
  currentVersionCache = '0.1.2'; // This should match your package.json version
  return currentVersionCache;
};

// Get current version from cache, localStorage, or fallback
export const getCurrentVersion = () => {
  if (currentVersionCache) {
    return currentVersionCache;
  }
  
  try {
    const storedVersionInfo = localStorage.getItem('app_version_info');
    if (storedVersionInfo) {
      const storedData = JSON.parse(storedVersionInfo);
      return storedData.version;
    }
  } catch (error) {
    console.warn('Failed to get stored version:', error);
  }
  
  // Fallback to package.json version
  return '0.1.2'; // This should match your package.json version
};

// Current frontend version - dynamically determined
export const CURRENT_VERSION = getCurrentVersion();

/**
 * Check for version updates using static file version checking
 * @returns {Promise<Object>} Version information from static file
 */
export const checkVersion = async () => {
  try {
    // Add timestamp to prevent caching
    const response = await fetch(`${VERSION_CHECK_CONFIG.STATIC_VERSION_FILE}?t=${Date.now()}`, {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const versionInfo = await response.json();
    
    // Get stored version info
    const storedVersionInfo = localStorage.getItem('app_version_info');
    const storedData = storedVersionInfo ? JSON.parse(storedVersionInfo) : null;
    
    // Check if version has changed
    const hasUpdate = storedData && (
      storedData.version !== versionInfo.version ||
      storedData.deploymentId !== versionInfo.deploymentId ||
      storedData.hash !== versionInfo.hash
    );
    
    // Store current version info
    localStorage.setItem('app_version_info', JSON.stringify(versionInfo));
    
    return {
      has_update: hasUpdate,
      latest_version: versionInfo.version,
      current_version: storedData?.version || getCurrentVersion(),
      build_time: versionInfo.buildTime,
      deployment_id: versionInfo.deploymentId,
      message: hasUpdate ? 'New version available' : 'Up to date'
    };
  } catch (error) {
    console.warn('Version check failed:', error);
    // Return current version if check fails to avoid false positives
    return { 
      has_update: false,
      latest_version: getCurrentVersion(),
      current_version: getCurrentVersion(),
      message: 'Version check unavailable'
    };
  }
};

/**
 * Check if an update is available based on server response
 * @param {Object} versionResponse - Response from version check API
 * @returns {boolean} True if update is available
 */
export const isUpdateAvailable = (versionResponse) => {
  if (!versionResponse || typeof versionResponse !== 'object') return false;
  
  // Check if the server response indicates an update is available
  return versionResponse.has_update === true;
};

/**
 * Get version check configuration
 */
export const getVersionCheckConfig = () => VERSION_CHECK_CONFIG; 